import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase, signInWithGoogle } from '../../services/supabase';
import './auth.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for success messages or errors from redirects
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success) {
      setSuccessMessage('Account created successfully! Please sign in.');
    }
    
    if (error) {
      switch (error) {
        case 'auth_failed':
          setError('Authentication failed. Please try again.');
          break;
        case 'user_exists':
          setError('An account with this email already exists. Please sign in.');
          break;
        default:
          setError('An error occurred. Please try again.');
      }
    }
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        switch (error.message) {
          case 'Invalid login credentials':
            throw new Error('Invalid email or password. Please try again.');
          case 'Email not confirmed':
            throw new Error('Please verify your email address before signing in.');
          case 'Too many requests':
            throw new Error('Too many login attempts. Please try again later.');
          default:
            throw error;
        }
      }

      if (data?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (profileError) {
          console.warn('Profile check error:', profileError);
        }

        if (profile) {
          navigate('/dashboard');
        } else {
          navigate('/profile');
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'An unexpected error occurred during sign in.';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await signInWithGoogle();
    } catch (error: any) {
      console.error('Google login error:', error);
      
      let errorMessage = 'Failed to sign in with Google. ';
      
      if (error.message.includes('popup')) {
        errorMessage += 'Popup was blocked. Please allow popups for this site.';
      } else if (error.message.includes('access_denied')) {
        errorMessage += 'Access was denied. Please try again.';
      } else {
        errorMessage += 'Please try again.';
      }
      
      setError(errorMessage);
      setGoogleLoading(false);
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        {/* Left Side - Welcome Section - UPDATED for Teach IDD */}
        <div className="welcome-section">
          <div className="welcome-content">
            <h1 className="welcome-title">
              Welcome Back,<br />
              <span className="brand-gradient">Teacher</span>
            </h1>
            <p className="welcome-subtitle">
              Sign in to Teach IDD — your AI assistant for supporting slow learners and students with intellectual and developmental disabilities.
            </p>
            <div className="feature-highlights">
              <div className="highlight-item">
                <span className="highlight-icon">🧠</span>
                <span>IDD Teaching Guide</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">📖</span>
                <span>Confusion Solver for Slow Learners</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">🗣️</span>
                <span>Read-Aloud Teacher Mode</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">📋</span>
                <span>No Diagnosis Required</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="form-section">
          <div className="form-container">
            <div className="form-header">
              <h2 className="form-title">Teach IDD</h2>
              <p className="form-subtitle">Sign in to support every learner in your classroom</p>
            </div>

            {successMessage && (
              <div className="success-message">
                <span className="success-icon">✅</span>
                {successMessage}
              </div>
            )}

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="auth-form">
              <div className="input-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-wrapper">
                  <input
                    id="email"
                    type="email"
                    placeholder="teacher@school.edu.ng"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="form-input"
                  />
                  <span className="input-icon">📧</span>
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="form-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <div className="forgot-password">
                  <Link to="/forgot-password" className="forgot-link">
                    Forgot password?
                  </Link>
                </div>
              </div>
              
              <button 
                type="submit" 
                disabled={loading}
                className={`login-btn ${loading ? 'loading' : ''}`}
              >
                {loading ? (
                  <span className="loading-content">
                    <span className="spinner"></span>
                    Signing In...
                  </span>
                ) : (
                  'Sign In to Teach IDD'
                )}
              </button>

              <div className="divider">
                <span>or continue with</span>
              </div>
              
              <button 
                type="button" 
                onClick={handleGoogleLogin} 
                disabled={googleLoading || loading}
                className="google-btn"
              >
                {googleLoading ? (
                  <span className="loading-content">
                    <span className="spinner"></span>
                    Redirecting...
                  </span>
                ) : (
                  <>
                    <svg className="google-icon" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                      <g>
                      <path fill="#4285F4" d="M44.5 20H24v8.5h11.7C34.7 33.6 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 6 .9 8.3 2.7l6.2-6.2C34.6 4.6 29.6 3 24 3 12.9 3 4 11.9 4 23s8.9 20 20 20c10.2 0 19-7.4 19-20 0-1.3-.1-2.7-.5-4z"/>
                      <path fill="#34A853" d="M6.3 14.7l7 5.1C15.4 16.1 19.3 13 24 13c3.1 0 6 .9 8.3 2.7l6.2-6.2C34.6 4.6 29.6 3 24 3c-7.2 0-13 5.8-13 13 0 2.3.6 4.5 1.3 6.7z"/>
                      <path fill="#FBBC05" d="M24 43c5.6 0 10.6-1.8 14.3-4.9l-6.6-5.4C29.7 34.9 27 36 24 36c-6.1 0-11.2-4.1-13-9.6l-7 5.4C7.4 39.4 15.1 43 24 43z"/>
                      <path fill="#EA4335" d="M44.5 20H24v8.5h11.7c-1.1 3.1-4.1 5.5-7.7 5.5-3.1 0-5.8-2.1-6.7-5.1l-7-5.4C13.1 32.9 18.1 37 24 37c5.6 0 10.6-1.8 14.3-4.9l-6.6-5.4C29.7 34.9 27 36 24 36c-6.1 0-11.2-4.1-13-9.6l-7 5.4C7.4 39.4 15.1 43 24 43c10.2 0 19-7.4 19-20 0-1.3-.1-2.7-.5-4z"/>
                      </g>
                    </svg>
                    Sign in with Google
                  </>
                )}
              </button>
            </form>

            <div className="form-footer">
              <p>
                New to Teach IDD? 
                <Link to="/signup" className="signup-link">
                  Create a Teacher Account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;